import React from 'react';
import { Tag, Space, Button, Popover, Popconfirm } from 'antd';
import {
  UserOutlined, EnvironmentOutlined, CalendarOutlined, EyeOutlined,
  WhatsAppOutlined, ArrowUpOutlined, DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { NdoeDataCard, NdoeActionButton, NdoeStatusBadge } from '../../components/ndoe';
import { formatCommandeCode, phoneOf } from '../commande/ListeCommande/commandeShared';

/* ---------------------------------------------------------------------------
   Helpers partagés par LivraisonJour et Livraison_detail
   (colonnes + cartes mobile communes ; évite la duplication).
--------------------------------------------------------------------------- */

const waHref = (phone) => `https://wa.me/${String(phone || '').replace(/[^0-9]/g, '')}`;

/* ⑨ Métadonnées des statuts de livraison (statut_livraison, 16 valeurs).
   `tone` = couleur du Tag antd (desktop). Source unique pour la colonne, la carte
   mobile ET les options du filtre multi-statuts. NULL hérité = AFFECTEE. */
export const LIVRAISON_STATUT_META = {
  AFFECTEE: { label: 'À prendre', tone: 'blue' },
  PRISE_EN_CHARGE: { label: 'Prise en charge', tone: 'cyan' },
  EN_COURS: { label: 'En cours', tone: 'processing' },
  REPORTEE: { label: 'Reportée', tone: 'orange' },
  NON_PRISE_EN_CHARGE: { label: 'Non prise', tone: 'volcano' },
  LIVREE: { label: 'Livrée', tone: 'green' },
  RETOUR_LIVRAISON: { label: 'Retour magasin', tone: 'red' },
  RETOUR_CLIENT: { label: 'Retour client', tone: 'red' },
  RETOUR_POUR_ECHANGE: { label: 'Retour pour échange', tone: 'purple' },
  ECHANGE_EN_COURS: { label: 'Échange en cours', tone: 'purple' },
  ECHANGE_A_LIVRER: { label: 'Échange à livrer', tone: 'geekblue' },
  ECHANGE_TERMINE: { label: 'Échange terminé', tone: 'green' },
  ECHANGE_ANNULE: { label: 'Échange annulé', tone: 'default' },
  ECHANGE_RETOURNE: { label: 'Échange retourné', tone: 'red' },
  AFFECTATION_LIBEREE: { label: 'Affectation libérée', tone: 'default' },
  AFFECTATION_EXPIREE: { label: 'Affectation expirée', tone: 'default' },
};

export const livraisonStatutMeta = (code) =>
  LIVRAISON_STATUT_META[code] || { label: code || '—', tone: 'default' };

/* Options prêtes pour un <Select mode="multiple"> (valeur = code, libellé = texte). */
export const LIVRAISON_STATUT_OPTIONS = Object.entries(LIVRAISON_STATUT_META)
  .map(([value, meta]) => ({ value, label: meta.label }));

/**
 * Colonnes tableau (desktop) communes aux listes de détails de livraison.
 * opts : { onClient, onView, onDelete, isAdmin, indexOffset, communeTone }
 */
export const buildLivraisonColumns = (opts = {}) => {
  const { onClient, onView, onDelete, isAdmin, indexOffset = 0, communeTone = 'green' } = opts;
  return [
    { title: '#', dataIndex: 'id', key: 'id', render: (t, r, index) => indexOffset + index + 1, width: '4%' },
    {
      title: 'Code', dataIndex: 'id_commande', key: 'id_commande',
      render: (text, record) => <Tag color="blue">{formatCommandeCode(record)}</Tag>,
    },
    {
      title: 'Client', dataIndex: 'nom_client', key: 'nom_client',
      render: (text, record) => (
        <div onClick={() => onClient && onClient(record.id_client)} style={{ cursor: onClient ? 'pointer' : 'default' }}>
          <Tag color="green"><UserOutlined style={{ marginRight: 5 }} />{text}</Tag>
        </div>
      ),
    },
    {
      title: 'Commune', dataIndex: 'nom_commune', key: 'nom_commune',
      render: (text) => <Tag color={communeTone}><EnvironmentOutlined style={{ marginRight: 5 }} />{text}</Tag>,
    },
    {
      title: 'Téléphone', dataIndex: 'telephone', key: 'telephone',
      render: (text) => (
        <Popover content="Discutez sur WhatsApp" placement="top">
          <Tag onClick={() => window.open(waHref(text), '_blank')} color="green" style={{ cursor: 'pointer' }}>
            <WhatsAppOutlined style={{ color: 'green', marginRight: 5 }} />{text}
          </Tag>
        </Popover>
      ),
    },
    {
      title: 'Livreur', dataIndex: 'nom_livreur', key: 'nom_livreur',
      render: (text) => <Tag color="green"><UserOutlined />{text}</Tag>,
    },
    {
      title: 'Statut', dataIndex: 'statut_livraison', key: 'statut_livraison',
      render: (code) => {
        const m = livraisonStatutMeta(code || 'AFFECTEE');
        return <Tag color={m.tone}>{m.label}</Tag>;
      },
    },
    {
      title: 'Quantité', dataIndex: 'quant', key: 'quant',
      sorter: (a, b) => a.quant - b.quant,
      sortDirections: ['descend', 'ascend'],
      render: (text, record) => (
        <Popover content="Voir les détails" placement="top">
          <Tag color="blue" icon={<ArrowUpOutlined />} style={{ cursor: onView ? 'pointer' : 'default' }} onClick={() => onView && onView(record.id_commande)}>
            {record.quant}
          </Tag>
        </Popover>
      ),
    },
    {
      title: 'Date & Heure', dataIndex: 'date_creation', key: 'date_creation',
      sorter: (a, b) => moment(a.date_creation).unix() - moment(b.date_creation).unix(),
      sortDirections: ['descend', 'ascend'],
      render: (text) => <Tag color="blue" icon={<CalendarOutlined />}>{moment(text).format('DD-MM-yyyy HH:mm')}</Tag>,
    },
    {
      title: 'Action', key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Popover title="Voir la livraison" trigger="hover">
            <Button icon={<EyeOutlined />} style={{ color: 'blue' }} onClick={() => onView && onView(record.id_commande)} />
          </Popover>
          {isAdmin && onDelete && (
            <Popover title="Supprimer" trigger="hover">
              <Popconfirm title="Êtes-vous sûr de vouloir supprimer?" onConfirm={() => onDelete(record.id_detail_livraison)} okText="Oui" cancelText="Non">
                <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Popover>
          )}
        </Space>
      ),
    },
  ];
};

/**
 * Carte livraison (mobile). handlers : { onView, onDelete, isAdmin }
 */
export const renderLivraisonCard = (record, handlers = {}) => {
  const { onView, onDelete, isAdmin } = handlers;
  const phone = record.telephone || phoneOf(record);
  const actions = [
    <NdoeActionButton key="v" size="sm" variant="ink" icon={<EyeOutlined />} onClick={() => onView && onView(record.id_commande)}>Détail</NdoeActionButton>,
    phone && <NdoeActionButton key="w" size="sm" variant="secondary" icon={<WhatsAppOutlined />} as="a" href={waHref(phone)} target="_blank" rel="noreferrer">WhatsApp</NdoeActionButton>,
    isAdmin && onDelete && (
      <Popconfirm key="d" title="Supprimer cette ligne ?" onConfirm={() => onDelete(record.id_detail_livraison)} okText="Oui" cancelText="Non">
        <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
      </Popconfirm>
    ),
  ].filter(Boolean);

  const statutTxt = livraisonStatutMeta(record.statut_livraison || 'AFFECTEE').label;
  return (
    <NdoeDataCard
      title={record.nom_client}
      subtitle={`${formatCommandeCode(record)}${record.nom_commune ? ' · ' + record.nom_commune : ''} · ${statutTxt}`}
      badge={record.nom_livreur ? <NdoeStatusBadge kind="custom" tone="info" label={record.nom_livreur} /> : null}
      metrics={[
        { value: record.quant, label: 'paires' },
        { value: record.date_creation ? moment(record.date_creation).format('DD-MM-yy HH:mm') : '—', label: 'le' },
      ]}
      onClick={() => onView && onView(record.id_commande)}
      actions={actions}
    />
  );
};
